// Cache service for API responses and component data
class CacheService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
  }

  // Set cache with TTL
  set(key, data, ttl = this.defaultTTL) {
    const timestamp = Date.now();
    this.cache.set(key, data);
    this.timestamps.set(key, timestamp + ttl);
  }

  // Get cache if not expired
  get(key) {
    const expiry = this.timestamps.get(key);
    if (!expiry || Date.now() > expiry) {
      this.delete(key);
      return null;
    }
    return this.cache.get(key);
  }

  // Delete cache entry
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  // Check if cache exists and is valid
  has(key) {
    const expiry = this.timestamps.get(key);
    return expiry && Date.now() <= expiry;
  }

  // Get cache size
  size() {
    return this.cache.size;
  }

  // Get all cache keys
  keys() {
    return Array.from(this.cache.keys());
  }

  // Set cache with custom TTL
  setWithTTL(key, data, ttl) {
    this.set(key, data, ttl);
  }

  // Cache for different data types
  setUserData(data) {
    this.set('user_data', data, 10 * 60 * 1000); // 10 minutes
  }

  getUserData() {
    return this.get('user_data');
  }

  setItemsData(data) {
    this.set('items_data', data, 5 * 60 * 1000); // 5 minutes
  }

  getItemsData() {
    return this.get('items_data');
  }

  setBranchesData(data) {
    this.set('branches_data', data, 10 * 60 * 1000); // 10 minutes
  }

  getBranchesData() {
    return this.get('branches_data');
  }

  setOrdersData(data) {
    this.set('orders_data', data, 2 * 60 * 1000); // 2 minutes
  }

  getOrdersData() {
    return this.get('orders_data');
  }

  setCategoriesData(data) {
    this.set('categories_data', data, 15 * 60 * 1000); // 15 minutes
  }

  getCategoriesData() {
    return this.get('categories_data');
  }

  setSectionsData(data) {
    this.set('sections_data', data, 15 * 60 * 1000); // 15 minutes
  }

  getSectionsData() {
    return this.get('sections_data');
  }

  // Clear specific data types
  clearUserData() {
    this.delete('user_data');
  }

  clearItemsData() {
    this.delete('items_data');
  }

  clearOrdersData() {
    this.delete('orders_data');
    this.delete('orders_simple_data');
    this.delete('orders_submission_data');
  }

  clearSectionsData() {
    this.delete('sections_data');
    this.delete('sections_active');
  }

  clearAllData() {
    this.clear();
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;